// src/encounter/wrap-up-pending-orders.tsx
// Order to Invoice T11 (PROD-2594, + the practitioner half of PROD-2630): the encounter's pending
// orders, under the wrap-up invoice panel. Rendered by `WrapUpView` in the `invoice` slot NEXT TO
// `WrapUpInvoiceSection` — the DS `WrapUpInvoicePanel` organism has no slot and does not change.
//
// Mirrors pm-react's invoice "Pending orders" section (PROD-2591, `invoice-pending-orders.tsx`):
// same Orders endpoints, same vaccine folding, same readiness rules, same copy. Manual only: an
// order reaches the invoice only when someone presses Add (confirmed, since it moves money) or is
// dismissed (not billed here; the clinical order is not cancelled). Dismissed orders can be
// restored.
//
// GATING — identical to the wrap-up invoice panel, no new permission:
//   visible  = `canSeeInvoice` (Show_Billing_Summary VIEW && !isNurse, `lib/wrap-up-invoice-gate`)
//   actions  = `canSeeInvoice && invoiceId && orders.canEdit` (Billing's Draft-only flag)
// With `canSeeInvoice` false nothing renders and no Orders request is made.
//
// HIDDEN on `state: "NoEncounter"` (device bookings) and when the plan has no orders at all.
// Every mutation invalidates the invoice detail key behind `useEncounterInvoice`, so the panel's
// lines and totals refresh after an Add (see `use-invoice-orders.ts`).
import { useMemo, useState } from 'react';
import { Button, Checkbox, Skeleton, StatusChip } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import {
  addItemsFor,
  addItemsWithPickedService,
  groupFee,
  groupOrderLines,
  groupReadiness,
  hasAnyOrders,
  itemRefsFor,
  type OrderGroup,
} from '@/billing/lib/pending-orders';
import { useInvoice } from '@/billing/services/use-invoice';
import {
  useAddOrders,
  useDismissOrders,
  useInvoiceOrders,
  useRestoreOrders,
} from '@/billing/services/use-invoice-orders';
import { formatMoney } from '@/shared/lib/money';
import { canActOnPendingOrders } from './lib/wrap-up-invoice-gate';
import { SPONSOR_TYPE_SELF_PAY } from './lib/to-invoice-section';
import { InvoiceAddServiceDialog } from './invoice-add-service-dialog';
import { WrapUpDismissedOrders } from './wrap-up-dismissed-orders';
import { PO_TH, PO_TH_V2, WrapUpPendingOrderRow } from './wrap-up-pending-order-row';
import { useDesignV2, V2_EYEBROW } from './lib/design-v2';
import { AddOrdersConfirmModal, DismissOrdersModal, type DismissChoice } from './wrap-up-pending-orders-dialogs';

export interface WrapUpPendingOrdersProps {
  /** The encounter's invoice (`useEncounterInvoice`). Null → nothing to read yet. */
  invoiceId: string | null;
  /** The wrap-up invoice gate (`canSeeWrapUpInvoice`). False → renders nothing, fetches nothing. */
  canSeeInvoice: boolean;
  /** Fallback currency until the Orders read answers with its own. */
  currency: string;
}

const EYEBROW =
  'm-0 text-size-eyebrow font-bold uppercase tracking-normal text-brand-depth';
/** Design sheet v1.1 §1 (switch ON only): the DS caps label in text-secondary (H11). */
const EYEBROW_V2 = `m-0 ${V2_EYEBROW}`;

export function WrapUpPendingOrders({ invoiceId, canSeeInvoice, currency: fallbackCurrency }: WrapUpPendingOrdersProps) {
  // Every hook sits above the early returns: the first render is a loading one, and a hook below
  // a return changes the hook count on the next render.
  const readId = canSeeInvoice ? invoiceId : null;
  const ordersQuery = useInvoiceOrders(readId);
  // Design sheet v1.1, switch ON only (`?ef=frameV2`); OFF renders today's labels.
  const designV2 = useDesignV2();
  const th = designV2 ? PO_TH_V2 : PO_TH;
  // Same key as the panel's own invoice read, so this is a cache hit, not a second request.
  const invoiceQuery = useInvoice({ invoiceId: readId ?? undefined });
  const user = useAuthStore((s) => s.user);
  const addOrders = useAddOrders();
  const dismissOrders = useDismissOrders();
  const restoreOrders = useRestoreOrders();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<OrderGroup[] | null>(null);
  const [dismissTarget, setDismissTarget] = useState<OrderGroup[] | null>(null);
  const [pickFor, setPickFor] = useState<OrderGroup | null>(null);

  const data = ordersQuery.data;
  const pendingGroups = useMemo(() => groupOrderLines(data?.pending ?? []), [data]);
  const dismissedGroups = useMemo(() => groupOrderLines(data?.dismissed ?? []), [data]);

  if (!readId) return null;
  if (ordersQuery.isLoading) return <PendingOrdersSkeleton />;
  if (!hasAnyOrders(data)) return null;

  const editable = canActOnPendingOrders(canSeeInvoice, invoiceId, data.canEdit);
  // Read-only (Issued / Cancelled) with nothing pending: nothing to show or act on.
  if (!editable && pendingGroups.length === 0) return null;

  const id = readId;
  const currency = data.currency || fallbackCurrency;
  const invoice = invoiceQuery.data;
  // Also busy while the list refetches (after every mutation, and after a 409): until the fresh
  // read lands, a row that was just added or dismissed is still on screen, and a second click on it
  // would only earn an AlreadyAdded / NotPending round trip.
  const busy =
    addOrders.isPending || dismissOrders.isPending || restoreOrders.isPending || ordersQuery.isFetching;
  // Derived, not stored: a selected row that left the list simply drops out of the selection.
  // PROD-2679 (mirrors pm-react PROD-2678): any pending row can be ticked, so Dismiss takes them
  // all; bulk Add and the indicative total take only the ready ones.
  const selectedGroups = pendingGroups.filter((g) => selected.has(g.key));
  const addableGroups = selectedGroups.filter((g) => groupReadiness(g, choices) === 'ready');
  const allSelected = pendingGroups.length > 0 && selectedGroups.length === pendingGroups.length;
  const selectedAmount = addableGroups.reduce((sum, g) => sum + (groupFee(g, choices) ?? 0), 0);
  const partialAdd = addableGroups.length < selectedGroups.length;
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'you';
  const selfPay = (invoice?.sponsorType ?? '').trim().toLowerCase() === SPONSOR_TYPE_SELF_PAY.toLowerCase();

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const removeFromSelection = (groups: OrderGroup[]) =>
    setSelected((prev) => {
      const next = new Set(prev);
      groups.forEach((g) => next.delete(g.key));
      return next;
    });

  const addedToast = (groups: OrderGroup[], payable: number | undefined) =>
    toast.success(
      groups.length === 1 ? `${groups[0].primary.display} added to the invoice` : `${groups.length} orders added to the invoice`,
      { description: payable !== undefined ? `Patient Payable ${currency} ${formatMoney(payable)}` : undefined },
    );

  const confirmAdd = () => {
    if (!confirm) return;
    const groups = confirm;
    addOrders.mutate(
      { invoiceId: id, request: { items: addItemsFor(groups, choices) } },
      {
        onSuccess: (res) => {
          addedToast(groups, res?.invoice?.patientPayable);
          removeFromSelection(groups);
          setConfirm(null);
        },
        // The hook toasts the error; close so the refetched list is visible.
        onError: () => setConfirm(null),
      },
    );
  };

  // NoPrice: the picker's Add is the confirmation — the chosen service rides as `serviceId`.
  const addWithPickedService = (group: OrderGroup, serviceId: string) =>
    addOrders.mutate(
      { invoiceId: id, request: { items: addItemsWithPickedService(group, choices, serviceId) } },
      { onSuccess: (res) => addedToast([group], res?.invoice?.patientPayable) },
    );

  const dismiss = async (choice: DismissChoice): Promise<boolean> => {
    const groups = dismissTarget ?? [];
    try {
      await dismissOrders.mutateAsync({
        invoiceId: id,
        request: { items: itemRefsFor(groups), reason: choice.reason, note: choice.note },
      });
      toast.success(
        groups.length === 1
          ? `${groups[0].primary.display} dismissed from this invoice`
          : `${groups.length} orders dismissed from this invoice`,
      );
      removeFromSelection(groups);
      return true;
    } catch {
      return false; // the hook toasts the server message
    }
  };

  const restore = (group: OrderGroup) =>
    restoreOrders.mutate(
      { invoiceId: id, request: { items: itemRefsFor([group]) } },
      { onSuccess: () => toast.success(`${group.primary.display} restored to pending orders`) },
    );

  return (
    <section aria-labelledby="wrap-up-pending-orders-heading" className="flex w-full flex-col gap-3" data-slot="wrap-up-pending-orders">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 id="wrap-up-pending-orders-heading" className={designV2 ? EYEBROW_V2 : EYEBROW}>
            Pending orders
          </h3>
          {pendingGroups.length > 0 && (
            <StatusChip semantic="neutral" tone="soft" size="xs">
              {pendingGroups.length}
            </StatusChip>
          )}
        </div>
        <span className="text-size-2xs text-text-hint">From Assessment &amp; Plan</span>
      </div>

      {!editable && (
        <p className="m-0 rounded-md bg-grey-100 px-4 py-3 text-size-sm text-text-secondary">Revert to draft to add orders.</p>
      )}

      {pendingGroups.length === 0 ? (
        <p className="m-0 py-2 text-size-sm text-text-secondary">
          No pending orders. Orders placed in Assessment &amp; Plan appear here with an indicative price.
        </p>
      ) : (
        <>
          <p className="m-0 text-size-2xs text-text-hint">
            Indicative prices. Not part of Patient Payable until someone adds them to the invoice. Dismiss leaves an
            order off this invoice without cancelling it.
          </p>

          {editable && selectedGroups.length > 0 && (
            <div
              role="toolbar"
              aria-label="Selected pending orders"
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md bg-grey-100 py-1.5 pr-2 pl-3"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-size-sm">
                <span className="font-bold tabular-nums text-brand-depth">{selectedGroups.length} selected</span>
                <span className="tabular-nums text-text-secondary">
                  {currency} {formatMoney(selectedAmount)} indicative
                </span>
                <Button variant="link" size="sm" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => setDismissTarget(selectedGroups)}>
                  Dismiss selected
                </Button>
                <Button
                  size="sm"
                  disabled={busy || addableGroups.length === 0}
                  onClick={() => setConfirm(addableGroups)}
                >
                  {partialAdd
                    ? `Add ${addableGroups.length} of ${selectedGroups.length} to invoice`
                    : 'Add selected to invoice'}
                </Button>
              </div>
            </div>
          )}

          <div data-slot="po-table" className="w-full overflow-x-auto">
            <table className="w-full min-w-160 border-collapse">
              <caption className="sr-only">Pending orders</caption>
              <thead>
                <tr>
                  {editable && (
                    <th className={`${th} w-8 pl-2`}>
                      <Checkbox
                        aria-label="Select all pending orders"
                        checked={allSelected}
                        disabled={busy}
                        onCheckedChange={(v) => setSelected(v ? new Set(pendingGroups.map((g) => g.key)) : new Set())}
                      />
                    </th>
                  )}
                  <th className={th}>Order</th>
                  <th className={th}>Type</th>
                  <th className={th}>Order status</th>
                  <th className={th}>Ordered by</th>
                  <th className={`${th} text-right normal-case`}>
                    <span className="uppercase">Indicative</span>, {currency}
                  </th>
                  {editable && (
                    <th className={th}>
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pendingGroups.map((g) => (
                  <WrapUpPendingOrderRow
                    key={g.key}
                    group={g}
                    choices={choices}
                    editable={editable}
                    busy={busy}
                    selected={selected.has(g.key)}
                    onToggle={toggle}
                    onChoose={(key, serviceId) => setChoices((prev) => ({ ...prev, [key]: serviceId }))}
                    onAdd={(group) => setConfirm([group])}
                    onPickService={setPickFor}
                    onDismiss={(group) => setDismissTarget([group])}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Kept apart from the invoice totals above, and never summed with them. */}
          <div
            aria-label="Pending orders (indicative)"
            className="flex items-baseline justify-between gap-6 rounded-md border border-dashed border-border-default px-4 py-3 text-size-sm"
          >
            <span className="text-text-secondary">Pending (indicative)</span>
            <span className="tabular-nums text-text-secondary">
              {currency} {formatMoney(data.pendingIndicativeTotal)}
            </span>
          </div>
        </>
      )}

      {editable && (
        <WrapUpDismissedOrders groups={dismissedGroups} currency={currency} editable={editable} busy={busy} onRestore={restore} />
      )}

      <AddOrdersConfirmModal
        target={
          confirm
            ? {
                names: confirm.map((g) => g.primary.display),
                amount: confirm.reduce((sum, g) => sum + (groupFee(g, choices) ?? 0), 0),
              }
            : null
        }
        currency={currency}
        patientPayable={invoice?.patientPayable}
        selfPay={selfPay}
        userName={userName}
        pending={addOrders.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={confirmAdd}
      />
      <DismissOrdersModal
        key={dismissTarget?.map((g) => g.key).join('|') ?? 'closed'}
        title={
          dismissTarget
            ? dismissTarget.length === 1
              ? `Dismiss ${dismissTarget[0].primary.display} from this invoice?`
              : `Dismiss ${dismissTarget.length} orders from this invoice?`
            : null
        }
        pending={dismissOrders.isPending}
        onCancel={() => setDismissTarget(null)}
        onConfirm={dismiss}
      />
      {pickFor && (
        // The existing wrap-up add-service picker, pre-filtered by the order's code; remounted per
        // order so the search is seeded fresh. The order carries its own Dx, so the invoice-level
        // "add diagnosis first" rule does not apply to it.
        <InvoiceAddServiceDialog
          key={pickFor.key}
          open
          title={`Choose a service for ${pickFor.primary.display}`}
          initialQuery={pickFor.primary.code}
          onClose={() => setPickFor(null)}
          existingServiceIds={(invoice?.services ?? []).map((s) => s.serviceId).filter((s): s is string => !!s)}
          hasDiagnosis
          onAdd={(serviceId) => addWithPickedService(pickFor, serviceId)}
          busy={addOrders.isPending}
          providerId={invoice?.providerId ?? invoice?.provider?.id}
          locationId={invoice?.locationId}
          patientId={invoice?.patientId ?? invoice?.patient?.id}
          currency={currency}
          scopeLoading={invoiceQuery.isPending}
        />
      )}
    </section>
  );
}

function PendingOrdersSkeleton() {
  return (
    <section aria-label="Loading pending orders" className="flex w-full flex-col gap-3">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </section>
  );
}
